import type { TResponseGetOperatingStatement } from "@/apis/odin/apiODIN";
import {
    formatOperatingStatementMonth,
    convertAPIResponseToDTOTableExtractionAnnualized,
} from "./convertAPIResponseToDTOTableExtractionAnnualized";
import type { TDtoTableExtractionAnnualized } from "./TDtoTableExtractionAnnualized";

type Statement = NonNullable<TResponseGetOperatingStatement>;
export type ReconciliationPeriod = {
    label: string;
    start: number;
    length: number;
    columns: { headerName: string; field: string; difference?: boolean }[];
};
export type Reconciliation = TDtoTableExtractionAnnualized & {
    reconciliationPeriods: ReconciliationPeriod[];
};

// The backend owns reconciliation arithmetic and period selection. This adapter
// only maps its rows, cells and period metadata to the grid's fields.
export const convertReconciliation = (
    statement: Statement | null,
    group: "month" | "quarter" | "half" | "year",
): Reconciliation | null => {
    if (!statement?.reconciliationPeriods) return null;
    const dto = convertAPIResponseToDTOTableExtractionAnnualized(
        {
            ...statement,
            columns: [],
            rowCells: [],
            netOperatingIncomeCells: [],
        },
        "month",
    );
    if (!dto) return null;
    const fieldFor = (order: number) => `reconciliation-${order}`;
    const reconciliationPeriods = statement.reconciliationPeriods.map(
        period => ({
            ...period,
            columns: period.columns.map(column => ({
                headerName: column.headerName,
                field: fieldFor(column.columnOrder),
                difference: column.difference,
            })),
        }),
    );
    const numberValue = (value: string | undefined): number | null => {
        if (value === undefined || value === "" || value === "None")
            return null;
        const number = Number(value);
        return Number.isFinite(number) ? Math.round(number * 100) : null;
    };
    const valuesByRow: Record<string, Record<string, number | null>> = {};
    statement.rowCells.forEach(cell => {
        const field = fieldFor(Number(cell["col_identifier"]));
        statement.rows.forEach(row => {
            (valuesByRow[row.rowOrder] ??= {})[field] = numberValue(
                cell[row.rowOrder],
            );
        });
    });
    const totalNetIncome: Record<string, number> = {};
    statement.netOperatingIncomeCells
        .filter(cell => cell.isActive)
        .forEach(cell => {
            const value = numberValue(cell.total);
            if (value !== null)
                totalNetIncome[fieldFor(cell.columnOrder)] = value;
        });
    let income = false;
    const favorableByRow = Object.fromEntries(
        statement.rows.toReversed().map(row => {
            if (row.rowType === "TYPE") income = row.name === "Total Income";
            return [row.rowOrder, income && !row.isNonOperating];
        }),
    );
    const columnDefsOperatingStatements: TDtoTableExtractionAnnualized["columnDefsOperatingStatements"] =
        {};
    reconciliationPeriods.forEach(period => {
        const year = Math.floor(period.start / 12);
        const quarter = `Q${Math.floor((period.start % 12) / 3) + 1}`;
        const half = `H${Math.floor((period.start % 12) / 6) + 1}`;
        const periodKey =
            period.length === 12 ? "Q1-Q4" : group === "half" ? half : quarter;
        const yearColumns = (columnDefsOperatingStatements[year] ??= {});
        const field = period.columns[0]?.field;
        if (field === undefined) return;
        yearColumns[periodKey] = [
            ...(yearColumns[periodKey] ?? []),
            {
                headerName:
                    period.length === 1
                        ? formatOperatingStatementMonth((period.start % 12) + 1)
                        : period.label,
                field,
                year,
                month:
                    period.length === 1
                        ? formatOperatingStatementMonth((period.start % 12) + 1)
                        : undefined,
                quarter: period.length === 3 ? quarter : undefined,
                half: period.length === 6 ? half : undefined,
                children: undefined,
                extractionId: undefined,
            },
        ];
    });
    const enrich = <
        T extends TDtoTableExtractionAnnualized["nonOperatingRows"][number],
    >(
        row: T,
    ) => ({
        ...row,
        ...valuesByRow[row.rowId],
        reconciliationFavorableAbove: favorableByRow[row.rowId] ?? false,
    });
    return {
        ...dto,
        rows: dto.rows.map(enrich),
        nonOperatingRows: dto.nonOperatingRows.map(enrich),
        totalNetIncome,
        reconciliationPeriods,
        columnDefsOperatingStatements,
    };
};
